import type { ColumnDefinition, EntityFilterConfig } from "../../../ui/components/Table/types";
import type { HistoryEvent } from "../../lib/api/history";
import type { TranslateValues } from "../../lib/i18n";

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function createHistoryTableColumns(
  t: (key: string, values?: TranslateValues) => string,
  entityFilters?: {
    user?: EntityFilterConfig;
    organization?: EntityFilterConfig;
    product?: EntityFilterConfig;
  },
): ColumnDefinition<HistoryEvent>[] {
  const cols: ColumnDefinition<HistoryEvent>[] = [
    {
      id: "occurredAt",
      label: t("pro.system.history.col.time"),
      sortable: true,
      sortKey: "occurredAt",
      pinnedLeft: true,
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {formatTime(row.occurredAt)}
        </span>
      ),
    },
    {
      id: "type",
      label: t("pro.system.history.col.type"),
      sortable: true,
      filterType: "checkbox",
      filterOptions: [
        { value: "audit", label: t("pro.system.history.type.audit") },
        { value: "security", label: t("pro.system.history.type.security") },
        { value: "error", label: t("pro.system.history.type.error") },
        {
          value: "notification",
          label: t("pro.system.history.type.notification"),
        },
      ],
      render: (row) => row.type,
    },
    {
      id: "source",
      label: t("pro.system.history.col.service"),
      filterType: "checkbox",
      filterOptions: [
        {
          value: "user-service",
          label: t("pro.system.history.service.userService"),
        },
        {
          value: "product-service",
          label: t("pro.system.history.service.productService"),
        },
      ],
      render: (row) => {
        if (row.source === "user-service") {
          return t("pro.system.history.service.userService");
        }
        if (row.source === "product-service") {
          return t("pro.system.history.service.productService");
        }
        return row.source ?? "—";
      },
    },
    {
      id: "domain",
      label: t("pro.system.history.col.domain"),
      sortable: true,
      render: (row) => row.domain,
    },
    {
      id: "event",
      label: t("pro.system.history.col.event"),
      sortable: true,
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{row.event}</span>
      ),
    },
    {
      id: "summary",
      label: t("pro.system.history.col.summary"),
      sortable: true,
      render: (row) => row.summary,
    },
  ];

  if (entityFilters?.user) {
    cols.push({
      id: "users",
      label: t("pro.system.history.col.user"),
      filterType: "entity",
      filterKey: "userIds",
      defaultHidden: true,
      entityFilter: entityFilters.user,
      render: () => null,
    });
  }
  if (entityFilters?.organization) {
    cols.push({
      id: "organizations",
      label: t("pro.system.history.col.organization"),
      filterType: "entity",
      filterKey: "organizationIds",
      defaultHidden: true,
      entityFilter: entityFilters.organization,
      render: () => null,
    });
  }
  if (entityFilters?.product) {
    cols.push({
      id: "products",
      label: t("pro.system.history.col.product"),
      filterType: "entity",
      filterKey: "productIds",
      defaultHidden: true,
      entityFilter: entityFilters.product,
      render: () => null,
    });
  }

  return cols;
}
